import {
	clearFetchError,
	createMobileFiltersController,
	getPageFromLink,
	isMobileViewport,
	readUrlListingState,
	showFetchError,
	syncUrlParams,
} from '../utils/filterable-archive.js';

export class VideoLibrary {
	constructor() {
		document.querySelectorAll('.video-library[data-ajax="1"]').forEach((root) => {
			this.bind(root);
		});
	}

	bind(root) {
		const config = window.icarVideoLibrary;

		if (!config?.ajaxUrl) {
			return;
		}

		const results = root.querySelector('.video-library__results');

		if (!results) {
			return;
		}

		const instanceId = root.dataset.instance || '1';
		const defaultPerPage = root.dataset.defaultPerPage || '9';
		const activeFilters = root.querySelector('[data-video-library-active-filters]');
		let pendingFilterSnapshot = null;
		let skipRevertOnClose = false;

		const getSelectedTopics = () =>
			[...root.querySelectorAll('.video-library__topic-input:checked')].map((input) => input.value);

		const getSelectedLanguages = () =>
			[...root.querySelectorAll('.video-library__lang-input:checked')].map((input) => input.value);

		const snapshotFilters = () => ({
			s: root.querySelector('.video-library__search-input')?.value ?? '',
			topics: getSelectedTopics(),
			languages: getSelectedLanguages(),
		});

		const restoreFilterSnapshot = (snapshot) => {
			const searchInput = root.querySelector('.video-library__search-input');

			if (searchInput) {
				searchInput.value = snapshot.s;
			}

			root.querySelectorAll('.video-library__topic-input').forEach((input) => {
				input.checked = snapshot.topics.includes(input.value);
			});

			root.querySelectorAll('.video-library__lang-input').forEach((input) => {
				input.checked = snapshot.languages.includes(input.value);
			});
		};

		const { filtersBackdrop, setFiltersOpen } = createMobileFiltersController(root, {
			blockName: 'video-library',
			backdropSelector: '[data-video-library-filters-backdrop]',
			toggleSelector: '[data-video-library-open-filters]',
			panelSelector: '.video-library__filters-panel',
			closeSelector: '[data-video-library-close-filters]',
			onWillClose: () => {
				if (!skipRevertOnClose && pendingFilterSnapshot) {
					restoreFilterSnapshot(pendingFilterSnapshot);
				}

				pendingFilterSnapshot = null;
				skipRevertOnClose = false;
			},
		});

		const setFiltersOpenWithRevert = (open, { keepChanges = false } = {}) => {
			if (open && isMobileViewport()) {
				pendingFilterSnapshot = snapshotFilters();
			}

			if (!open) {
				skipRevertOnClose = keepChanges;
			}

			setFiltersOpen(open);
		};

		const updateActiveFilters = (html) => {
			if (!activeFilters || html === undefined) {
				return;
			}

			activeFilters.innerHTML = html;
		};

		const appendListToBody = (body, key, values) => {
			values.forEach((slug) => {
				body.append(`${key}[]`, slug);
			});
		};

		const applyStateToForm = (state) => {
			const searchInput = root.querySelector('.video-library__search-input');
			const perPageSelect = root.querySelector('.video-library__per-page-select');

			if (searchInput) {
				searchInput.value = state.s || '';
			}

			if (perPageSelect) {
				perPageSelect.value = state.posts_per_page || defaultPerPage;
			}

			const topics = state.video_topics || [];
			const languages = state.video_lang || [];

			root.querySelectorAll('.video-library__topic-input').forEach((input) => {
				input.checked = topics.includes(input.value);
			});

			root.querySelectorAll('.video-library__lang-input').forEach((input) => {
				input.checked = languages.includes(input.value);
			});
		};

		const fetchResults = async (params, { updateUrl = true, scroll = false, closeFilters = false } = {}) => {
			root.classList.add('is-loading');
			clearFetchError(results, 'video-library');

			const body = new FormData();
			body.append('action', config.action);
			body.append('nonce', config.nonce);
			body.append('default_per_page', defaultPerPage);
			body.append('instance_id', instanceId);

			const topics = params.video_topics || [];
			const languages = params.video_lang || [];

			Object.entries(params).forEach(([key, value]) => {
				if (
					key === 'video_topics' ||
					key === 'video_lang' ||
					value === undefined ||
					value === null ||
					value === ''
				) {
					return;
				}

				body.append(key, String(value));
			});

			appendListToBody(body, 'video_topics', topics);
			appendListToBody(body, 'video_lang', languages);

			try {
				const response = await fetch(config.ajaxUrl, {
					method: 'POST',
					body,
					credentials: 'same-origin',
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}

				const payload = await response.json();

				if (!payload?.success) {
					throw new Error('Request failed');
				}

				const main = results.querySelector('.video-library__main');

				if (main && payload.data.html) {
					main.outerHTML = payload.data.html;
				}

				if (payload.data.footerHtml) {
					const footer = root.querySelector('[data-video-library-footer]');

					if (footer) {
						footer.innerHTML = payload.data.footerHtml;
					}
				}

				if (payload.data.countHtml) {
					const count = root.querySelector('.video-library__sidebar .video-library__count');

					if (count) {
						count.outerHTML = payload.data.countHtml;
					}
				}

				updateActiveFilters(payload.data.activeFiltersHtml);

				if (updateUrl) {
					this.updateUrl(params, defaultPerPage);
				}

				if (scroll) {
					root.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}

				if (closeFilters) {
					setFiltersOpenWithRevert(false, { keepChanges: true });
				}
			} catch {
				showFetchError(
					results,
					'video-library',
					'Unable to load videos. Please try again.'
				);
			} finally {
				root.classList.remove('is-loading');
			}
		};

		const getCurrentState = (paged = '1') => {
			const searchInput = root.querySelector('.video-library__search-input');
			const perPageSelect = root.querySelector('.video-library__per-page-select');

			return {
				s: searchInput?.value?.trim() || '',
				video_topics: getSelectedTopics(),
				video_lang: getSelectedLanguages(),
				posts_per_page: perPageSelect?.value || defaultPerPage,
				paged: String(paged),
			};
		};

		const clearTopicInputs = () => {
			root.querySelectorAll('.video-library__topic-input').forEach((input) => {
				input.checked = false;
			});
		};

		const clearLanguageInputs = () => {
			root.querySelectorAll('.video-library__lang-input').forEach((input) => {
				input.checked = false;
			});
		};

		const uncheckTopic = (slug) => {
			root.querySelectorAll(`.video-library__topic-input[value="${slug}"]`).forEach((input) => {
				input.checked = false;
			});
		};

		const uncheckLanguage = (slug) => {
			root.querySelectorAll(`.video-library__lang-input[value="${slug}"]`).forEach((input) => {
				input.checked = false;
			});
		};

		const onPopState = () => {
			if (!root.isConnected) {
				return;
			}

			const state = readUrlListingState(defaultPerPage, {
				listParams: [
					{ param: 'video_topics', stateKey: 'video_topics' },
					{ param: 'video_lang', stateKey: 'video_lang' },
				],
			});
			applyStateToForm(state);
			fetchResults(state, { updateUrl: false });
		};

		window.addEventListener('popstate', onPopState);

		root.addEventListener('click', (event) => {
			const openButton = event.target.closest('[data-video-library-open-filters]');

			if (openButton && root.contains(openButton)) {
				event.preventDefault();
				setFiltersOpenWithRevert(true);
				return;
			}

			const closeButton = event.target.closest('[data-video-library-close-filters]');

			if (closeButton && root.contains(closeButton)) {
				event.preventDefault();
				setFiltersOpenWithRevert(false);
				return;
			}

			if (event.target === filtersBackdrop) {
				event.preventDefault();
				setFiltersOpenWithRevert(false);
				return;
			}

			const removeSearch = event.target.closest('[data-video-library-remove-search]');

			if (removeSearch && root.contains(removeSearch)) {
				event.preventDefault();

				const searchInput = root.querySelector('.video-library__search-input');

				if (searchInput) {
					searchInput.value = '';
				}

				const state = getCurrentState();
				state.s = '';

				fetchResults(state, { scroll: true });
				return;
			}

			const removeTopic = event.target.closest('[data-video-library-remove-topic]');

			if (removeTopic && root.contains(removeTopic)) {
				event.preventDefault();

				const slug = removeTopic.getAttribute('data-video-library-remove-topic');

				if (slug) {
					uncheckTopic(slug);
				}

				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const removeLang = event.target.closest('[data-video-library-remove-lang]');

			if (removeLang && root.contains(removeLang)) {
				event.preventDefault();

				const slug = removeLang.getAttribute('data-video-library-remove-lang');

				if (slug) {
					uncheckLanguage(slug);
				}

				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const clearButton = event.target.closest('[data-video-library-clear]');

			if (clearButton && root.contains(clearButton)) {
				event.preventDefault();

				const searchInput = root.querySelector('.video-library__search-input');
				const perPageSelect = root.querySelector('.video-library__per-page-select');

				if (searchInput) {
					searchInput.value = '';
				}

				if (perPageSelect) {
					perPageSelect.value = defaultPerPage;
				}

				clearTopicInputs();
				clearLanguageInputs();

				fetchResults(
					{
						s: '',
						video_topics: [],
						video_lang: [],
						posts_per_page: defaultPerPage,
						paged: '1',
					},
					{ scroll: true, closeFilters: true }
				);
				return;
			}

			const applyButton = event.target.closest('[data-video-library-apply-filters]');

			if (applyButton && root.contains(applyButton)) {
				event.preventDefault();
				fetchResults(getCurrentState(), { scroll: true, closeFilters: true });
				return;
			}

			const copyButton = event.target.closest('.share-social__copy');

			if (copyButton && root.contains(copyButton)) {
				event.preventDefault();
				const link = copyButton.dataset.link;
				if (!link) {
					return;
				}
				navigator.clipboard.writeText(link);
				return;
			}

			const pageLink = event.target.closest('.video-library__pagination a');

			if (pageLink && root.contains(pageLink)) {
				event.preventDefault();
				const state = getCurrentState(getPageFromLink(pageLink));
				fetchResults(state, { scroll: true });
			}
		});

		const searchForm = root.querySelector('.video-library__search');
		const searchInput = root.querySelector('.video-library__search-input');

		if (searchForm) {
			searchForm.addEventListener('submit', (event) => {
				event.preventDefault();
				fetchResults(getCurrentState(), { scroll: true, closeFilters: true });
			});
		}

		if (searchInput) {
			let lastSearch = searchInput.value.trim();

			searchInput.addEventListener('input', () => {
				const value = searchInput.value.trim();

				if (value === '' && lastSearch !== '') {
					const state = getCurrentState();
					state.s = '';
					fetchResults(state);
				}

				lastSearch = value;
			});
		}

		root.addEventListener('change', (event) => {
			const filterInput = event.target.closest(
				'.video-library__topic-input, .video-library__lang-input'
			);

			if (filterInput && root.contains(filterInput) && !isMobileViewport()) {
				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const select = event.target.closest('.video-library__per-page-select');

			if (!select || !root.contains(select)) {
				return;
			}

			fetchResults(getCurrentState(), { scroll: true });
		});
	}

	updateUrl(params, defaultPerPage) {
		const url = new URL(window.location.href);

		syncUrlParams(url, params, defaultPerPage, ['video_topics', 'video_lang'], (targetUrl, state) => {
			if (state.video_topics?.length) {
				targetUrl.searchParams.set('video_topics', state.video_topics.join(','));
			}

			if (state.video_lang?.length) {
				targetUrl.searchParams.set('video_lang', state.video_lang.join(','));
			}
		});

		window.history.pushState({}, '', url);
	}
}
